import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import { Spinner } from './UI';

/**
 * Editor de paquetes compartido entre Nueva Orden y Presupuestos.
 * mostrarCalculadora: si true, muestra la calculadora de costo (repuestos+MO+comisión ML+IVA)
 * para sugerir el precio — usada en Presupuestos.
 */
export default function EditorPaquetes({ paquetes, setPaquetes, vehiculo, mostrarCalculadora = false }) {
  const [urlML, setUrlML] = useState('');
  const [cargandoML, setCargandoML] = useState(false);
  const [mlError, setMlError] = useState('');

  const [mostrarPegarTexto, setMostrarPegarTexto] = useState(false);
  const [textoML, setTextoML] = useState('');
  const [cargandoTextoML, setCargandoTextoML] = useState(false);
  const [textoMLError, setTextoMLError] = useState('');

  const [catalogo, setCatalogo] = useState([]);
  const [busqCatalogo, setBusqCatalogo] = useState('');
  const [mostrarCatalogo, setMostrarCatalogo] = useState(false);

  const [manual, setManual] = useState({ nombre: '', precio: '' });
  const [cargandoSugerencia, setCargandoSugerencia] = useState(false);

  const [internoAbierto, setInternoAbierto] = useState({});
  const [calculadoraAbierta, setCalculadoraAbierta] = useState({});

  const [leyendoFoto, setLeyendoFoto] = useState(null);

  const [historialModelo, setHistorialModelo] = useState(null);
  const [cargandoHistorial, setCargandoHistorial] = useState(false);

  const [confirmandoBorrarTarea, setConfirmandoBorrarTarea] = useState(null); // { iPaq, iTarea, repuestosCoincidentes }

  useEffect(() => {
    api('/servicios/').then(setCatalogo).catch(() => {});
  }, []);

  const catalogoFiltrado = catalogo.filter(s =>
    s.nombre.toLowerCase().includes(busqCatalogo.toLowerCase())
  );

  const paqueteBase = () => ({
    costo_repuestos: 0, costo_mano_obra: 0, comision_ml_pct: 15, iva_pct: 22,
  });

  // ── Leer ML ────────────────────────────────────────────────────
  const leerML = async () => {
    if (!urlML.trim()) return;
    setCargandoML(true); setMlError('');
    try {
      const res = await api('/importar/leer-ml', {
        method: 'POST',
        body: JSON.stringify({
          url: urlML,
          marca: vehiculo?.marca,
          modelo: vehiculo?.modelo,
        })
      });
      const nuevoPaquete = {
        nombre: res.nombre_paquete || 'Service',
        precio: res.precio || 0,
        tareas: (res.tareas || []).map(t => ({ descripcion: t })),
        repuestos: (res.repuestos || []).map(r => ({
          nombre: r.nombre, detalle: r.detalle || '', codigo: '', costo_taller: '', costo: ''
        })),
        ...paqueteBase(),
      };
      setPaquetes(p => [...p, nuevoPaquete]);
      setUrlML('');
    } catch (e) {
      setMlError(e.message || 'No se pudo leer el link. Probá pegando el link nuevamente.');
    }
    setCargandoML(false);
  };

  // ── Pegar texto de la publicación (alternativa al link) ──────────
  const procesarTextoML = async () => {
    if (!textoML.trim()) return;
    setCargandoTextoML(true); setTextoMLError('');
    try {
      const res = await api('/importar/procesar-texto-ml', {
        method: 'POST',
        body: JSON.stringify({
          texto: textoML,
          marca: vehiculo?.marca,
          modelo: vehiculo?.modelo,
        })
      });
      const nuevoPaquete = {
        nombre: res.nombre_paquete || 'Service',
        precio: res.precio || 0,
        tareas: (res.tareas || []).map(t => ({ descripcion: t })),
        repuestos: (res.repuestos || []).map(r => ({
          nombre: r.nombre, detalle: r.detalle || '', codigo: '', costo_taller: '', costo: ''
        })),
        ...paqueteBase(),
      };
      setPaquetes(p => [...p, nuevoPaquete]);
      setTextoML('');
      setMostrarPegarTexto(false);
    } catch (e) {
      setTextoMLError(e.message || 'No se pudo procesar el texto.');
    }
    setCargandoTextoML(false);
  };
  const agregarTextoManual = () => {
    if (!textoML.trim()) return;
    const primeraLinea = textoML.trim().split('\n')[0].slice(0, 120);
    setPaquetes(p => [...p, {
      nombre: primeraLinea || 'Service',
      precio: 0,
      tareas: [],
      repuestos: [],
      ...paqueteBase(),
    }]);
    setTextoML('');
    setMostrarPegarTexto(false);
  };
  const agregarDelCatalogo = (srv) => {
    const tareasDelCatalogo = (srv.tareas && srv.tareas.length > 0)
      ? srv.tareas.map(t => ({ descripcion: t.descripcion }))
      : (srv.descripcion ? [{ descripcion: srv.descripcion }] : []);
    setPaquetes(p => [...p, {
      nombre: srv.nombre,
      precio: srv.precio || 0,
      tareas: tareasDelCatalogo,
      repuestos: (srv.repuestos || []).map(r => ({
        nombre: r.nombre, detalle: r.detalle || '', codigo: '', costo_taller: '', costo: ''
      })),
      ...paqueteBase(),
    }]);
    setMostrarCatalogo(false);
  };

  // ── Agregar manual con sugerencia IA ────────────────────────────
  const agregarManual = async () => {
    if (!manual.nombre.trim()) return;
    setCargandoSugerencia(true);
    let repuestosSugeridos = [];
    try {
      const res = await api('/importar/sugerir-repuestos', {
        method: 'POST',
        body: JSON.stringify({
          descripcion_servicio: manual.nombre,
          marca: vehiculo?.marca,
          modelo: vehiculo?.modelo,
        })
      });
      repuestosSugeridos = (res.repuestos || []).map(r => ({
        nombre: r.nombre, detalle: r.detalle || '', codigo: '', costo_taller: '', costo: ''
      }));
    } catch { }

    setPaquetes(p => [...p, {
      nombre: manual.nombre,
      precio: parseFloat(manual.precio) || 0,
      tareas: [],
      repuestos: repuestosSugeridos,
      ...paqueteBase(),
    }]);
    setManual({ nombre: '', precio: '' });
    setCargandoSugerencia(false);
  };

  // ── Edición de paquetes ──────────────────────────────────────────
  const eliminarPaquete = (i) => setPaquetes(p => p.filter((_, idx) => idx !== i));
  const actualizarPaquete = (i, cambios) => setPaquetes(p => p.map((x, idx) => idx === i ? { ...x, ...cambios } : x));

  const actualizarTarea = (iPaq, iTarea, valor) => {
    setPaquetes(p => p.map((paq, idx) => {
      if (idx !== iPaq) return paq;
      return { ...paq, tareas: paq.tareas.map((t, j) => j === iTarea ? { ...t, descripcion: valor } : t) };
    }));
  };
  // Palabras genéricas que no cuentan como "coincidencia" (muy comunes, darían falsos positivos)
  const PALABRAS_IGNORADAS = new Set([
    'de', 'del', 'la', 'el', 'los', 'las', 'y', 'con', 'sin', 'para', 'en', 'a',
    'cambio', 'revision', 'revisión', 'control', 'inspeccion', 'inspección'
  ]);

  const normalizarPalabras = (texto) =>
    texto.toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '') // sin tildes
      .split(/\s+/)
      .filter(w => w.length > 2 && !PALABRAS_IGNORADAS.has(w));

  const buscarRepuestosCoincidentes = (tareaTexto, repuestos) => {
    const palabrasTarea = normalizarPalabras(tareaTexto);
    return repuestos
      .map((r, idx) => ({ r, idx }))
      .filter(({ r }) => {
        const palabrasRep = normalizarPalabras(r.nombre);
        return palabrasRep.some(p => palabrasTarea.includes(p));
      });
  };

  const eliminarTarea = (iPaq, iTarea) => {
    const paquete = paquetes[iPaq];
    const tareaTexto = paquete.tareas[iTarea]?.descripcion || '';
    const coincidencias = buscarRepuestosCoincidentes(tareaTexto, paquete.repuestos);

    if (coincidencias.length > 0) {
      setConfirmandoBorrarTarea({ iPaq, iTarea, coincidencias, tareaTexto });
    } else {
      setPaquetes(p => p.map((paq, idx) => idx !== iPaq ? paq : { ...paq, tareas: paq.tareas.filter((_, j) => j !== iTarea) }));
    }
  };

  const confirmarBorrarTareaYRepuestos = (borrarRepuestosTambien) => {
    const { iPaq, iTarea, coincidencias } = confirmandoBorrarTarea;
    const indicesRepuestosABorrar = new Set(coincidencias.map(c => c.idx));
    setPaquetes(p => p.map((paq, idx) => {
      if (idx !== iPaq) return paq;
      return {
        ...paq,
        tareas: paq.tareas.filter((_, j) => j !== iTarea),
        repuestos: borrarRepuestosTambien
          ? paq.repuestos.filter((_, j) => !indicesRepuestosABorrar.has(j))
          : paq.repuestos,
      };
    }));
    setConfirmandoBorrarTarea(null);
  };
  const agregarTarea = (iPaq) => {
    setPaquetes(p => p.map((paq, idx) => idx !== iPaq ? paq : { ...paq, tareas: [...paq.tareas, { descripcion: '' }] }));
  };

  const actualizarRepuesto = (iPaq, iRep, campo, valor) => {
    setPaquetes(p => p.map((paq, idx) => {
      if (idx !== iPaq) return paq;
      return { ...paq, repuestos: paq.repuestos.map((r, j) => j === iRep ? { ...r, [campo]: valor } : r) };
    }));
  };
  const eliminarRepuesto = (iPaq, iRep) => {
    setPaquetes(p => p.map((paq, idx) => idx !== iPaq ? paq : { ...paq, repuestos: paq.repuestos.filter((_, j) => j !== iRep) }));
  };
  const agregarRepuesto = (iPaq) => {
    setPaquetes(p => p.map((paq, idx) => idx !== iPaq ? paq : {
      ...paq, repuestos: [...paq.repuestos, { nombre: '', detalle: '', codigo: '', costo_taller: '', costo: '' }]
    }));
  };

  // ── Foto del código de repuesto ──────────────────────────────────
  const handleFotoRepuesto = async (iPaq, file) => {
    if (!file) return;
    setLeyendoFoto(iPaq);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch((process.env.REACT_APP_API_URL || '') + '/importar/leer-codigo-repuesto', {
        method: 'POST', body: formData,
      });
      const data = await res.json();
      if (data.nombre || data.codigo) {
        setPaquetes(p => p.map((paq, idx) => idx !== iPaq ? paq : {
          ...paq,
          repuestos: [...paq.repuestos, {
            nombre: data.nombre || '(repuesto sin identificar)',
            detalle: data.marca || '', codigo: data.codigo || '', costo_taller: '', costo: ''
          }]
        }));
        setInternoAbierto(s => ({ ...s, [iPaq]: true }));
      }
    } catch {
      alert('No se pudo leer la foto. Probá de nuevo o cargalo a mano.');
    }
    setLeyendoFoto(null);
  };

  // ── Historial por modelo ──────────────────────────────────────────
  const verHistorialModelo = async () => {
    const { marca, modelo } = vehiculo || {};
    if (!marca || !modelo) return;
    setCargandoHistorial(true);
    try {
      const res = await api(`/importar/catalogo-modelo/${encodeURIComponent(marca)}/${encodeURIComponent(modelo)}`);
      setHistorialModelo(res);
    } catch {
      setHistorialModelo([]);
    }
    setCargandoHistorial(false);
  };

  // ── Calculadora de costos (solo presupuestos) ────────────────────
  const calcularPrecioSugerido = (paq) => {
    const repuestos = parseFloat(paq.costo_repuestos) || 0;
    const manoObra = parseFloat(paq.costo_mano_obra) || 0;
    const comisionPct = parseFloat(paq.comision_ml_pct) || 0;
    const ivaPct = parseFloat(paq.iva_pct) || 0;
    const subtotal = repuestos + manoObra;
    const conComision = subtotal * (1 + comisionPct / 100);
    const conIva = conComision * (1 + ivaPct / 100);
    return Math.round(conIva);
  };

  const aplicarPrecioSugerido = (iPaq) => {
    const paq = paquetes[iPaq];
    const sugerido = calcularPrecioSugerido(paq);
    actualizarPaquete(iPaq, { precio: sugerido });
  };

  return (
    <div>
      {vehiculo?.marca && (
        <button className="btn btn-outline btn-sm" disabled={cargandoHistorial} onClick={verHistorialModelo}
          style={{ marginBottom: '1rem' }}>
          {cargandoHistorial ? '...' : `📜 Historial ${vehiculo.marca} ${vehiculo.modelo}`}
        </button>
      )}

      {historialModelo !== null && (
        <div className="card" style={{ marginBottom: '1rem' }}>
          <div className="sec-header" style={{ marginBottom: '.5rem' }}>
            <p style={{ fontWeight: 600, color: 'var(--azul)' }}>
              Repuestos históricos — {vehiculo.marca} {vehiculo.modelo}
            </p>
            <button className="btn btn-secondary btn-sm" onClick={() => setHistorialModelo(null)}>Cerrar</button>
          </div>
          {historialModelo.length === 0 ? (
            <p style={{ color: 'var(--gris-4)', fontSize: '.875rem' }}>Todavía no hay historial para este modelo.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '.3rem' }}>
              {historialModelo.map((r, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.8125rem',
                                      padding: '.3rem 0', borderBottom: '1px solid var(--gris-2)' }}>
                  <span>{r.nombre} {r.codigo ? `(${r.codigo})` : ''} {r.proveedor ? `— ${r.proveedor}` : ''}</span>
                  <span style={{ color: 'var(--gris-4)' }}>${r.ultimo_costo?.toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Link ML */}
      <div className="card" style={{ marginBottom: '1rem' }}>
        <p style={{ fontWeight: 600, marginBottom: '.6rem', color: 'var(--azul)' }}>Cargar desde Mercado Libre</p>
        <div style={{ display: 'flex', gap: '.5rem' }}>
          <input className="form-input" placeholder="https://articulo.mercadolibre.com.uy/..."
            value={urlML} onChange={e => setUrlML(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && leerML()} />
          <button className="btn btn-primary" disabled={cargandoML || !urlML} onClick={leerML}
            style={{ whiteSpace: 'nowrap' }}>
            {cargandoML ? '...' : 'Leer'}
          </button>
        </div>
        {cargandoML && <Spinner texto="Leyendo publicación con IA..." />}
        {mlError && (
          <div style={{ marginTop: '.5rem' }}>
            <p style={{ color: 'var(--rojo)', fontSize: '.875rem' }}>{mlError}</p>
            <p style={{ fontSize: '.8125rem', color: 'var(--gris-4)', marginTop: '.3rem' }}>
              Mercado Libre a veces bloquea la lectura automática. Probá la opción de abajo.
            </p>
          </div>
        )}

        <div style={{ marginTop: '.75rem', paddingTop: '.75rem', borderTop: '1px solid var(--gris-2)' }}>
          <button className="btn btn-outline btn-sm" onClick={() => setMostrarPegarTexto(v => !v)}>
            📋 {mostrarPegarTexto ? 'Cerrar' : '¿No funcionó? Pegar el texto de la publicación'}
          </button>
          {mostrarPegarTexto && (
            <div style={{ marginTop: '.6rem' }}>
              <p style={{ fontSize: '.8125rem', color: 'var(--gris-4)', marginBottom: '.5rem' }}>
                Abrí la publicación en Mercado Libre, seleccioná todo el texto (Ctrl+A), copialo (Ctrl+C) y pegalo aquí (Ctrl+V).
              </p>
              <textarea className="form-textarea" rows={5} placeholder="Pegá aquí el texto completo de la publicación..."
                value={textoML} onChange={e => setTextoML(e.target.value)} style={{ marginBottom: '.5rem' }} />
              <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap' }}>
                <button className="btn btn-primary btn-sm" disabled={cargandoTextoML || !textoML.trim()} onClick={procesarTextoML}>
                  {cargandoTextoML ? 'Procesando...' : '✨ Agregar con IA (título, tareas y repuestos)'}
                </button>
                <button className="btn btn-outline btn-sm" disabled={!textoML.trim()} onClick={agregarTextoManual}>
                  + Agregar igual (sin IA)
                </button>
              </div>
              {cargandoTextoML && <Spinner texto="Extrayendo información..." />}
              {textoMLError && <p style={{ color: 'var(--rojo)', fontSize: '.875rem', marginTop: '.5rem' }}>{textoMLError}</p>}
            </div>
          )}
        </div>
      </div>

      {/* Catálogo */}
      <div className="card" style={{ marginBottom: '1rem' }}>
        <div className="sec-header" style={{ marginBottom: mostrarCatalogo ? '.6rem' : 0 }}>
          <p style={{ fontWeight: 600, color: 'var(--azul)' }}>O elegir del catálogo</p>
          <button className="btn btn-outline btn-sm" onClick={() => setMostrarCatalogo(v => !v)}>
            📋 {mostrarCatalogo ? 'Cerrar' : 'Ver catálogo'}
          </button>
        </div>
        {mostrarCatalogo && (
          <div>
            <input className="form-input" placeholder="Buscar en catálogo..."
              value={busqCatalogo} onChange={e => setBusqCatalogo(e.target.value)}
              style={{ marginBottom: '.5rem' }} />
            <div style={{ maxHeight: 180, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '.25rem' }}>
              {catalogoFiltrado.map(s => (
                <button key={s.id} className="btn btn-outline btn-sm" style={{ justifyContent: 'flex-start' }}
                  onClick={() => agregarDelCatalogo(s)}>
                  {s.nombre} {s.precio ? `— $${s.precio.toLocaleString()}` : ''}
                </button>
              ))}
              {catalogoFiltrado.length === 0 && <p style={{ color: 'var(--gris-4)', fontSize: '.875rem' }}>Sin resultados</p>}
            </div>
          </div>
        )}
      </div>

      {/* Manual */}
      <div className="card" style={{ marginBottom: '1rem' }}>
        <p style={{ fontWeight: 600, marginBottom: '.6rem', color: 'var(--azul)' }}>O agregar a mano</p>
        <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap' }}>
          <input className="form-input" placeholder="Ej: Cambio de discos y pastillas de freno"
            style={{ flex: 2, minWidth: 200 }}
            value={manual.nombre} onChange={e => setManual(p => ({ ...p, nombre: e.target.value }))}
            onKeyDown={e => e.key === 'Enter' && agregarManual()} />
          <input type="number" className="form-input" placeholder="Precio $" style={{ width: 110 }}
            value={manual.precio} onChange={e => setManual(p => ({ ...p, precio: e.target.value }))} />
          <button className="btn btn-outline" disabled={cargandoSugerencia || !manual.nombre} onClick={agregarManual}>
            {cargandoSugerencia ? '...' : '+ Agregar'}
          </button>
        </div>
        {cargandoSugerencia && <Spinner texto="Buscando repuestos típicos..." />}
      </div>

      {/* Lista de paquetes */}
      {paquetes.map((paq, iPaq) => (
        <div key={iPaq} className="card" style={{ marginBottom: '1rem', borderLeft: '3px solid var(--azul-medio)' }}>
          <div style={{ display: 'flex', gap: '.5rem', alignItems: 'flex-start', marginBottom: '.75rem' }}>
            <input className="form-input" style={{ flex: 1, fontWeight: 600 }}
              value={paq.nombre} onChange={e => actualizarPaquete(iPaq, { nombre: e.target.value })} />
            <input type="number" className="form-input" placeholder="Precio $" style={{ width: 110 }}
              value={paq.precio} onChange={e => actualizarPaquete(iPaq, { precio: e.target.value })} />
            <button onClick={() => eliminarPaquete(iPaq)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--rojo)', fontSize: '1.1rem' }}>✕</button>
          </div>

          <p style={{ fontSize: '.8125rem', fontWeight: 600, color: 'var(--gris-5)', marginBottom: '.4rem' }}>Tareas incluidas</p>
          {paq.tareas.map((t, iT) => (
            <div key={iT} style={{ display: 'flex', gap: '.4rem', marginBottom: '.3rem' }}>
              <textarea className="form-textarea" rows={t.descripcion.length > 80 ? 2 : 1}
                style={{ flex: 1, fontSize: '.8125rem', minHeight: 36 }}
                value={t.descripcion} onChange={e => actualizarTarea(iPaq, iT, e.target.value)} />
              <button onClick={() => eliminarTarea(iPaq, iT)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--rojo)' }}>✕</button>
            </div>
          ))}
          <button className="btn btn-outline btn-sm" onClick={() => agregarTarea(iPaq)} style={{ marginBottom: '.75rem' }}>
            + Agregar tarea
          </button>

          <p style={{ fontSize: '.8125rem', fontWeight: 600, color: 'var(--gris-5)', marginBottom: '.4rem' }}>
            Repuestos utilizados
          </p>
          {paq.repuestos.map((r, iR) => (
            <div key={iR} style={{ display: 'flex', gap: '.4rem', marginBottom: '.3rem', flexWrap: 'wrap' }}>
              <input className="form-input" placeholder="Nombre del repuesto" style={{ flex: 1, minWidth: 140, fontSize: '.8125rem' }}
                value={r.nombre} onChange={e => actualizarRepuesto(iPaq, iR, 'nombre', e.target.value)} />
              <input className="form-input" placeholder="Detalle (marca, tipo, unidades)" style={{ flex: 1, minWidth: 140, fontSize: '.8125rem' }}
                value={r.detalle} onChange={e => actualizarRepuesto(iPaq, iR, 'detalle', e.target.value)} />
              <button onClick={() => eliminarRepuesto(iPaq, iR)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--rojo)' }}>✕</button>
            </div>
          ))}
          <button className="btn btn-outline btn-sm" onClick={() => agregarRepuesto(iPaq)} style={{ marginBottom: '.5rem', marginRight: '.4rem' }}>
            + Agregar repuesto
          </button>
          <label className="btn btn-outline btn-sm" style={{ marginBottom: '.5rem', cursor: 'pointer' }}>
            {leyendoFoto === iPaq ? '... leyendo' : '📷 Foto del código'}
            <input type="file" accept="image/*" capture="environment" style={{ display: 'none' }}
              disabled={leyendoFoto !== null}
              onChange={e => handleFotoRepuesto(iPaq, e.target.files[0])} />
          </label>

          {paq.repuestos.length > 0 && !mostrarCalculadora && (
            <div style={{ marginTop: '.5rem' }}>
              <button className="btn btn-secondary btn-sm"
                onClick={() => setInternoAbierto(s => ({ ...s, [iPaq]: !s[iPaq] }))}>
                {internoAbierto[iPaq] ? '▼' : '▶'} 🔒 Registro interno (códigos y costos — no se imprime)
              </button>
              {internoAbierto[iPaq] && (
                <div style={{ marginTop: '.5rem', padding: '.6rem', background: 'var(--gris-1)', borderRadius: 'var(--radio)' }}>
                  {paq.repuestos.map((r, iR) => (
                    <div key={iR} style={{ display: 'flex', gap: '.4rem', marginBottom: '.3rem', alignItems: 'center' }}>
                      <span style={{ flex: 1, fontSize: '.8125rem', color: 'var(--gris-5)' }}>{r.nombre || '(sin nombre)'}</span>
                      <input className="form-input" placeholder="Código" style={{ width: 110, fontSize: '.8125rem' }}
                        value={r.codigo} onChange={e => actualizarRepuesto(iPaq, iR, 'codigo', e.target.value)} />
                      <input type="number" className="form-input" placeholder="Costo $" style={{ width: 100, fontSize: '.8125rem' }}
                        value={r.costo_taller} onChange={e => actualizarRepuesto(iPaq, iR, 'costo_taller', e.target.value)} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Calculadora de costos (solo presupuestos) */}
          {mostrarCalculadora && (
            <div style={{ marginTop: '.75rem' }}>
              <button className="btn btn-secondary btn-sm"
                onClick={() => setCalculadoraAbierta(s => ({ ...s, [iPaq]: !s[iPaq] }))}>
                {calculadoraAbierta[iPaq] ? '▼' : '▶'} 🧮 Calculadora de costo (sugerir precio)
              </button>
              {calculadoraAbierta[iPaq] && (
                <div style={{ marginTop: '.5rem', padding: '.75rem', background: 'var(--gris-1)', borderRadius: 'var(--radio)' }}>
                  <div className="form-grid-2" style={{ marginBottom: '.5rem' }}>
                    <div className="form-group">
                      <label className="form-label">Costo repuestos (total)</label>
                      <input type="number" className="form-input" value={paq.costo_repuestos}
                        onChange={e => actualizarPaquete(iPaq, { costo_repuestos: e.target.value })} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Costo mano de obra</label>
                      <input type="number" className="form-input" value={paq.costo_mano_obra}
                        onChange={e => actualizarPaquete(iPaq, { costo_mano_obra: e.target.value })} />
                    </div>
                  </div>
                  <div className="form-grid-2" style={{ marginBottom: '.75rem' }}>
                    <div className="form-group">
                      <label className="form-label">Comisión ML %</label>
                      <input type="number" className="form-input" value={paq.comision_ml_pct}
                        onChange={e => actualizarPaquete(iPaq, { comision_ml_pct: e.target.value })} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">IVA %</label>
                      <input type="number" className="form-input" value={paq.iva_pct}
                        onChange={e => actualizarPaquete(iPaq, { iva_pct: e.target.value })} />
                    </div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '.875rem', color: 'var(--gris-5)' }}>
                      Precio sugerido: <strong>${calcularPrecioSugerido(paq).toLocaleString()}</strong>
                    </span>
                    <button className="btn btn-primary btn-sm" onClick={() => aplicarPrecioSugerido(iPaq)}>
                      Usar este precio
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      ))}

      {confirmandoBorrarTarea && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200
        }}>
          <div className="card" style={{ maxWidth: 420, width: '90%' }}>
            <p style={{ marginBottom: '.5rem', fontWeight: 600 }}>¿Eliminar también el repuesto relacionado?</p>
            <p style={{ marginBottom: '1rem', fontSize: '.875rem', color: 'var(--gris-5)' }}>
              Al borrar la tarea "{confirmandoBorrarTarea.tareaTexto}", encontramos que podría estar relacionada con:
            </p>
            <ul style={{ marginBottom: '1.25rem', paddingLeft: '1.2rem', fontSize: '.875rem' }}>
              {confirmandoBorrarTarea.coincidencias.map(({ r }, i) => (
                <li key={i}>{r.nombre}</li>
              ))}
            </ul>
            <div style={{ display: 'flex', gap: '.5rem', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button className="btn btn-secondary btn-sm" onClick={() => setConfirmandoBorrarTarea(null)}>Cancelar</button>
              <button className="btn btn-outline btn-sm" onClick={() => confirmarBorrarTareaYRepuestos(false)}>
                Solo borrar la tarea
              </button>
              <button className="btn btn-danger btn-sm" onClick={() => confirmarBorrarTareaYRepuestos(true)}>
                Borrar ambos
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
